/** Generate a graded list of intervals from any given scale degree to another,
 * including octave leaps. This is meant to be a starting place and to allow
 * further changes to the idea of "difficulty" based on user interaction. Only
 * scale degrees within a major or minor key are currently included.
 */
const dichordStrings: string[] = [];
const tonicArray = [1, 3, 5];
const domArray = [2, 5, 7];
const domSevArray = [2, 4, 5, 7];
const subdomArray = [1, 4, 6];
const supTonArray = [2, 4, 6];
const medArray = [3, 5, 7];
const subMedArray = [1, 3, 6];

export interface DichordQuestion {
  firstScaleDegree: number;
  /** coefficient [0,1] */
  difficulty: number;
  secondScaleDegree: number;
  direction: 'up' | 'down' | 'same';
  /** traditional Interval (1 = unison) */
  genericDistance: number;
  /** chromatic distance 0 = unison. */
  chromaticDistance: number;
  /** 'P','m','M','d','A' should be sufficient for now */
  intervalQuality: 'P' | 'm' | 'M' | 'd' | 'A';
  ottmanRank: number;
  id: string;
  /** All the modes the dichord exists in: major, naturalMinor, harmonicMinor, melodicMinor, mixture */
  modes: ('major' | 'harmonicMinor' | 'naturalMinor' | 'melodicMinor' | 'mixture')[];
  /** If this is a leap, whether it's a tonic, dominant or predominant leap */
  leapType: 'tonic' | 'dominant' | 'predominant' | null;
}

function earlySet() {
  for (let i = 1; i < 11; i++) {
    let first = i.toString();
    if (i === 8) {
      first = 'm3';
    } else if (i === 9) {
      first = 'm6';
    } else if (i === 10) {
      first = 'm7';
    }
    for (let j = 1; j < 11; j++) {
      let second = j.toString();
      if (j === 8) {
        second = 'm3';
      } else if (j === 9) {
        second = 'm6';
      } else if (j === 10) {
        second = 'm7';
      }
      if (i === j) {
        dichordStrings.push(first + second);
        dichordStrings.push(`${first}+${second}`);
        dichordStrings.push(`${first}-${second}`);
      } else {
        dichordStrings.push(`${first}+${second}`);
        dichordStrings.push(`${first}-${second}`);
      }
    }
  }
}

earlySet();

type QuestionWithoutDifficulty = Omit<DichordQuestion, 'difficulty'>;

function dichordToQuestionWithoutDifficulty(dichord: string): QuestionWithoutDifficulty {
  const chromaticArray = ['1', 'm2', '2', 'm3', '3', '4', 'd5', '5', 'm6', '6', 'm7', '7'];
  let firstScaleDegree = 0;
  const secondScaleDegree = Number(dichord.slice(-1));
  const secondScaleDegreeMinor = dichord.slice(-2, -1) === 'm';
  const firstScaleDegreeMinor = dichord[0] === 'm';
  let firstChromaticId: string;
  let secondChromaticId: string;
  /** This is the actual musical interval. 1 = unison */
  let genericDistance = 1;
  let chromaticDistance = 0;
  let intervalQuality: DichordQuestion['intervalQuality'];
  if (firstScaleDegreeMinor) {
    firstScaleDegree = Number(dichord[1]);
    firstChromaticId = dichord.slice(0, 2);
  } else {
    firstScaleDegree = Number(dichord[0]);
    firstChromaticId = dichord[0];
  }
  if (secondScaleDegreeMinor) {
    secondChromaticId = dichord.slice(-2);
  } else {
    secondChromaticId = dichord.slice(-1);
  }

  let direction: 'up' | 'down' | 'same' = 'same';

  // determine up or down AND generic interval

  if (dichord.includes('+')) {
    direction = 'up';
    if (firstScaleDegree === secondScaleDegree) {
      if (firstScaleDegreeMinor && !secondScaleDegreeMinor) {
        genericDistance = 1;
        chromaticDistance = 1;
      } else if (!firstScaleDegreeMinor && secondScaleDegreeMinor) {
        genericDistance = 8;
        chromaticDistance = 11;
      } else {
        genericDistance = 8;
        chromaticDistance = 12;
      }
    } else if (firstScaleDegree > secondScaleDegree) {
      genericDistance = 8 - (firstScaleDegree - secondScaleDegree);
      chromaticDistance =
        12 - chromaticArray.indexOf(firstChromaticId) + chromaticArray.indexOf(secondChromaticId);
    } else {
      genericDistance = 1 + secondScaleDegree - firstScaleDegree;
      chromaticDistance =
        chromaticArray.indexOf(secondChromaticId) - chromaticArray.indexOf(firstChromaticId);
    }
  } else if (dichord.includes('-')) {
    direction = 'down';
    if (firstScaleDegree === secondScaleDegree) {
      if (!firstScaleDegreeMinor && secondScaleDegreeMinor) {
        genericDistance = 1;
        chromaticDistance = 1;
      } else if (firstScaleDegreeMinor && !secondScaleDegreeMinor) {
        genericDistance = 8;
        chromaticDistance = 11;
      } else {
        genericDistance = 8;
        chromaticDistance = 12;
      }
    } else if (firstScaleDegree < secondScaleDegree) {
      genericDistance = 8 - (secondScaleDegree - firstScaleDegree);
      chromaticDistance =
        12 - chromaticArray.indexOf(secondChromaticId) + chromaticArray.indexOf(firstChromaticId);
    } else {
      genericDistance = 1 + firstScaleDegree - secondScaleDegree;
      chromaticDistance =
        chromaticArray.indexOf(firstChromaticId) - chromaticArray.indexOf(secondChromaticId);
    }
  }
  const intervalQualities = [0, 0, 2, 4, 5, 7, 9, 11, 12]; // made index = interval
  const perfectIntervals = [1, 4, 5, 8];
  if (perfectIntervals.includes(genericDistance)) {
    if (chromaticDistance === intervalQualities[genericDistance]) {
      intervalQuality = 'P';
    } else if (chromaticDistance === intervalQualities[genericDistance] - 1) {
      intervalQuality = 'd';
    } else if (chromaticDistance === intervalQualities[genericDistance] + 1) {
      intervalQuality = 'A';
    }
  } else if (!perfectIntervals.includes(genericDistance)) {
    if (chromaticDistance === intervalQualities[genericDistance]) {
      intervalQuality = 'M';
    } else if (chromaticDistance === intervalQualities[genericDistance] - 1) {
      intervalQuality = 'm';
    } else if (chromaticDistance === intervalQualities[genericDistance] + 1) {
      intervalQuality = 'A';
    } else if (chromaticDistance === intervalQualities[genericDistance] - 2) {
      intervalQuality = 'd';
    }
  }

  // Ottman Ranking
  // ordered by difficulty in Diatonic Spaces according to Ottman
  // octaves are included when they are part of the harmonic sets.

  /** ottmanRank is now from 0 to 9, which is appended as a decimal to an interval ranking for difficulty. */
  let ottmanRank = 0; // anything failed to be captured would have this rank
  // 0 unisons first
  if (genericDistance === 1) ottmanRank = 0;
  // 1 then stepwise (direction matters)
  else if (genericDistance === 2) ottmanRank = 1;
  // 2 then all sets where both numbers are in [1,3,5] (includes octaves)
  else if (tonicArray.includes(firstScaleDegree) && tonicArray.includes(secondScaleDegree))
    ottmanRank = 2;
  // 3 then all remaining sets where both numbers are in [2,5,7]
  else if (domArray.includes(firstScaleDegree) && domArray.includes(secondScaleDegree))
    ottmanRank = 3;
  // 4 then all remaining sets where both numbers are in [2,4,5,7]
  else if (domSevArray.includes(firstScaleDegree) && domSevArray.includes(secondScaleDegree))
    ottmanRank = 4;
  // 5 then all remaining sets where both numbers are in [1,4,6]
  else if (subdomArray.includes(firstScaleDegree) && subdomArray.includes(secondScaleDegree))
    ottmanRank = 5;
  // 6 then all remaining sets where both numbers are in [2,4,6] (2 and 6)
  else if (supTonArray.includes(firstScaleDegree) && supTonArray.includes(secondScaleDegree))
    ottmanRank = 6;
  // 7 Ottman doesn't address mediant or submediant triads, so we add them.
  else if (medArray.includes(firstScaleDegree) && medArray.includes(secondScaleDegree))
    ottmanRank = 7;
  // 8 Anything not covered (most 7ths) go last.
  else if (subMedArray.includes(firstScaleDegree) && subMedArray.includes(secondScaleDegree))
    ottmanRank = 8;
  else ottmanRank = 9;

  let leapType: 'tonic' | 'dominant' | 'predominant' | null = null;

  if (genericDistance === 1 || genericDistance === 8) leapType = null;
  else if (tonicArray.includes(firstScaleDegree) && tonicArray.includes(secondScaleDegree))
    leapType = 'tonic';
  else if ([2, 4, 5, 7].includes(firstScaleDegree) && [2, 4, 5, 7].includes(secondScaleDegree))
    leapType = 'dominant';
  else if (
    [2, 4, 6, 1, 3, 5].includes(firstScaleDegree) &&
    [2, 4, 6, 1, 3, 5].includes(secondScaleDegree)
  )
    leapType = 'predominant';

  const hasFlatSix =
    (firstScaleDegree === 6 && firstScaleDegreeMinor) ||
    (secondScaleDegree === 6 && secondScaleDegreeMinor);
  const hasRaisedSix =
    (firstScaleDegree === 6 && !firstScaleDegreeMinor) ||
    (secondScaleDegree === 6 && !secondScaleDegreeMinor);

  const hasFlatSeven =
    (firstScaleDegree === 7 && firstScaleDegreeMinor) ||
    (secondScaleDegree === 7 && secondScaleDegreeMinor);

  const hasRaisedSeven =
    (firstScaleDegree === 7 && !firstScaleDegreeMinor) ||
    (secondScaleDegree === 7 && !secondScaleDegreeMinor);

  const hasRaisedThree =
    (firstScaleDegree === 3 && !firstScaleDegreeMinor) ||
    (secondScaleDegree === 3 && !secondScaleDegreeMinor);

  const firstScaleDegreeIsMajorThree = firstScaleDegree === 3 && !firstScaleDegreeMinor;
  const secondScaleDegreeIsMajorThree = secondScaleDegree === 3 && !secondScaleDegreeMinor;

  const isMixture =
    (firstScaleDegreeIsMajorThree && secondScaleDegreeMinor) ||
    (secondScaleDegreeIsMajorThree && firstScaleDegreeMinor);

  const firstScaleDegreeIsMajorOnly =
    !firstScaleDegreeMinor &&
    (firstScaleDegree === 6 || firstScaleDegree === 7 || firstScaleDegree === 3);
  const secondScaleDegreeIsMajorOnly =
    !secondScaleDegreeMinor &&
    (secondScaleDegree === 6 || secondScaleDegree === 7 || secondScaleDegree === 3);

  const isNaturalMinor = !firstScaleDegreeIsMajorOnly && !secondScaleDegreeIsMajorOnly;

  // Are the two notes specifically within b6 6 b7 7, where one is raised the other lowered?
  const isMelodicMinor =
    !hasRaisedThree &&
    (isNaturalMinor || ((hasRaisedSeven || hasRaisedSix) && (hasFlatSeven || hasFlatSix)));

  const isHarmonicMinor = !hasRaisedSix && !hasFlatSeven && !hasRaisedThree;

  const isMinor = firstScaleDegreeMinor || secondScaleDegreeMinor;
  const isMajor = !isMinor;

  const modes: DichordQuestion['modes'] = [];
  if (isNaturalMinor) modes.push('naturalMinor');
  if (isHarmonicMinor) modes.push('harmonicMinor');
  if (isMelodicMinor) modes.push('melodicMinor');
  if (isMajor) modes.push('major');
  if (isMixture) modes.push('mixture');

  return {
    firstScaleDegree,
    secondScaleDegree,
    direction,
    genericDistance,
    chromaticDistance,
    intervalQuality,
    ottmanRank,
    id: dichord,
    modes,
    leapType,
  };
}

/** Sort based on interval size, and then ottman Rank.
 *  Results in unisons from 0.0 to 0.043
 *  2nds from 0.043 to 0.176
 *  3rds from 0.177 to 0.3
 */
const difficultySort = (a: QuestionWithoutDifficulty, b: QuestionWithoutDifficulty) =>
  a.genericDistance + a.ottmanRank / 10 - (b.genericDistance + b.ottmanRank / 10);
const ndf = (
  question: QuestionWithoutDifficulty,
  i: number,
  a: QuestionWithoutDifficulty[]
): DichordQuestion => ({
  ...question,
  difficulty: i / a.length,
});

/** move augmented unisons to most difficult because not consistent with logical sort */
const overrideDifficultySort = function (
  a: QuestionWithoutDifficulty,
  b: QuestionWithoutDifficulty
) {
  if (
    a.genericDistance === 1 &&
    a.chromaticDistance === 1 &&
    !(b.chromaticDistance === 1 && b.genericDistance === 1)
  )
    return 1;
  if (
    !(a.genericDistance === 1 && a.chromaticDistance === 1) &&
    b.chromaticDistance === 1 &&
    b.genericDistance === 1
  )
    return -1;
  return 0;
};

export const dichords = dichordStrings
  .map(dichordToQuestionWithoutDifficulty)
  .sort(difficultySort)
  .sort(overrideDifficultySort)
  .map(ndf);

// console.log(dichords);

// const newDichords = dichords.map((q) => q.id);

// console.log(newDichords);
